/**
 * AddPersonPolicyRoleModal — Phase 4b-2 commit 6 (2026-06-16, #11).
 *
 * The INVERSE of AddPolicyPartyModal: here THIS PERSON is fixed and the user
 * picks the POLICY (+ role + beneficiary fields). Opened from
 * PersonDetailPage's Roles tab ("Direct roles on policies" → "+ Add role");
 * stages a CREATE op onto the page's pending-policy-party queue, committed
 * through maker-checker on Save (approach A1).
 *
 * Mirrors AddPolicyPartyModal's field structure: Policy, Role, and the
 * BENEFICIARY-only Designation / Share / Date (blanked when the role isn't
 * BENEFICIARY so the staged payload can't trip the DB CHECK
 * ipp_non_beneficiary_no_designation). Editing an existing policy role still
 * happens on its own detail page (/insurance-policy-parties/:id).
 *
 * C-113: rendered inside PersonDetailPage's <form id="person-form"> subtree,
 * so the inner submit must stopPropagation or it also fires the page Save.
 */
import {
  Alert, Button, Group, Modal, NumberInput, Select, Stack, Text, Textarea, TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import * as React from 'react';

import { InsurancePolicyPicker } from './EntityPicker';
import type {
  BeneficiaryDesignation, InsurancePolicyPartyInput, InsurancePolicyPartyRole, UUID,
} from '../api/types';

const ROLE_OPTS: { value: InsurancePolicyPartyRole; label: string }[] = [
  { value: 'POLICY_HOLDER', label: 'Policy Holder' },
  { value: 'LIFE_INSURED', label: 'Life Insured' },
  { value: 'BENEFICIARY', label: 'Beneficiary' },
];

const DESIGNATION_OPTS: { value: BeneficiaryDesignation; label: string }[] = [
  { value: 'PRIMARY', label: 'Primary' },
  { value: 'CONTINGENT', label: 'Contingent' },
];

interface FormValues {
  policy_id: string;
  role: InsurancePolicyPartyRole;
  designation: string;
  percentage_share: number | '';
  designation_date: string;
  notes: string;
}

const EMPTY: FormValues = {
  policy_id: '',
  role: 'BENEFICIARY',
  designation: 'PRIMARY',
  percentage_share: '',
  designation_date: '',
  notes: '',
};

interface Props {
  opened: boolean;
  onClose: () => void;
  /** The person this role attaches — fixed (this page). */
  personId: UUID;
  personLabel: string;
  /** Called with the built payload; the parent appends a CREATE op with a
   *  freshly-minted tempKey to its pending-policy-party queue. */
  onStage: (payload: InsurancePolicyPartyInput) => void;
}

export function AddPersonPolicyRoleModal({
  opened, onClose, personId, personLabel, onStage,
}: Props) {
  const form = useForm<FormValues>({
    initialValues: EMPTY,
    validate: {
      policy_id: (v) => (v.length >= 32 ? null : 'Policy required'),
      designation: (v, all) =>
        all.role === 'BENEFICIARY' && !v ? 'Required for beneficiaries' : null,
    },
  });

  React.useEffect(() => {
    if (opened) form.setValues(EMPTY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  // Role-driven field gating: blank the beneficiary-only fields when the
  // role isn't BENEFICIARY so the staged payload can't carry stale values
  // (DB CHECK ipp_non_beneficiary_no_designation would reject on commit).
  const role = form.values.role;
  React.useEffect(() => {
    if (role !== 'BENEFICIARY') {
      if (form.values.designation) form.setFieldValue('designation', '');
      if (form.values.percentage_share !== '') form.setFieldValue('percentage_share', '');
      if (form.values.designation_date) form.setFieldValue('designation_date', '');
    } else if (!form.values.designation) {
      form.setFieldValue('designation', 'PRIMARY');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  function handleSave(values: FormValues) {
    const payload: InsurancePolicyPartyInput = {
      policy_id: values.policy_id,
      person_id: personId,
      role: values.role,
    };
    if (values.role === 'BENEFICIARY') {
      payload.designation = (values.designation || 'PRIMARY') as BeneficiaryDesignation;
      payload.percentage_share =
        values.percentage_share === '' ? null : values.percentage_share;
      payload.designation_date = values.designation_date || null;
    } else {
      payload.designation = null;
      payload.percentage_share = null;
      payload.designation_date = null;
    }
    if (values.notes) payload.notes = values.notes;
    onStage(payload);
    onClose();
  }

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.stopPropagation();
    form.onSubmit(handleSave)(e);
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Add a policy role for this person" size="lg">
      <form onSubmit={handleFormSubmit}>
        <Stack>
          <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
            <Text size="sm" fw={500}>{personLabel}</Text>
            <Text size="xs" c="dimmed">
              The new role is staged and only commits when you click Save
              Changes on the person page.
            </Text>
          </Alert>

          <InsurancePolicyPicker
            label="Insurance policy"
            description="Pick the policy to attach this person to in the selected role."
            value={form.values.policy_id || null}
            onChange={(v) => form.setFieldValue('policy_id', v ?? '')}
            error={form.errors.policy_id as string | undefined}
          />

          <Select
            label="Role"
            data={ROLE_OPTS}
            value={form.values.role}
            onChange={(v) => v && form.setFieldValue('role', v as InsurancePolicyPartyRole)}
            allowDeselect={false}
            required
          />

          {form.values.role === 'BENEFICIARY' && (
            <Stack gap="xs">
              <Group grow align="flex-start">
                <Select
                  label="Designation"
                  data={DESIGNATION_OPTS}
                  value={form.values.designation || null}
                  onChange={(v) => form.setFieldValue('designation', v ?? '')}
                  error={form.errors.designation as string | undefined}
                  required
                />
                <NumberInput
                  label="Percentage share"
                  description="Sums to 100% across PRIMARY beneficiaries on the policy."
                  value={form.values.percentage_share}
                  onChange={(v) =>
                    form.setFieldValue('percentage_share', typeof v === 'number' ? v : '')
                  }
                  min={0}
                  max={100}
                  decimalScale={2}
                />
                <TextInput
                  label="Designation date"
                  type="date"
                  value={form.values.designation_date}
                  onChange={(e) => form.setFieldValue('designation_date', e.currentTarget.value)}
                />
              </Group>
            </Stack>
          )}

          <Textarea
            label="Notes"
            placeholder="Optional"
            value={form.values.notes}
            onChange={(e) => form.setFieldValue('notes', e.currentTarget.value)}
            autosize
            minRows={2}
          />

          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button type="submit">Stage role</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
